import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useGraphQLRequest } from 'shared/auth/hooks/useApiRequest';
import FeedbackDashboardFilterBar, {
  FeedbackFilters,
} from 'shared/components/Feedback/FeedbackDashboardFilterBar';
import { ApplyFilters } from 'shared/components/Feedback/FiltersDropdown/ShowFilter';
import styled from 'styled-components';
import {
  FeedbackApi,
  feedbackMapFromApi,
  getFeedback,
  getSelectedSort
} from './api';
import FeedbackRows from './FeedbackRows';
import {
  tempAdminSortBy,
  tempCycleType,
  tempDonorName,
  tempDonorSortBy,
  tempDueDate,
  tempYear,
  tempStatus,
  tempTrueImpactStaff,
  tempCharityNavigator
} from './tempData';
import { useClient } from 'shared/apollo';
import { ApolloClient } from 'apollo-boost';
import { FeedbackDashboardCardProps } from 'shared/components/Feedback/FeedbackDashboardCard';
import {
  getPortfolioFilters,
  getRelationshipManager,
  getDonorsFilters,
} from 'shared/components/Feedback/FiltersDropdown/api';
import { mapPortfolioFilters, mapRelationshipFilters, mapDonorFilters } from './utils';

const StyledDiv = styled.div`
  .filter-bar {
    width: 100%;
  }
`;

const tempSortBy = (type: FeedbackDashboardProps['type']) => {
  return type === 'donor'
    ? { ...tempDonorSortBy() }
    : {
      ...tempAdminSortBy()
    };
};

const getDefaultSortBy = (
  type: FeedbackDashboardProps['type']
): FeedbackFilters => {
  const defaultSort = tempSortBy(type);
  defaultSort['Program Name by A-Z'].selected = true;
  return defaultSort;
};

const isAnyFilterApplied = (
  donorsFilter: FeedbackFilters,
  relationshipFilter: FeedbackFilters,
  portfolioFilter: FeedbackFilters,
  cyclesFilter: FeedbackFilters,
  statusFilter: FeedbackFilters,
  staffFilter: FeedbackFilters,
  dueDateFilter: FeedbackFilters,
  charityNavigatorFilter: FeedbackFilters,
  investmentFilter: FeedbackFilters
): boolean => {
  for (const f in donorsFilter) {
    if (donorsFilter[f].selected) {
      return true;
    }
  }
  for (const f in relationshipFilter) {
    if (relationshipFilter[f].selected) {
      return true;
    }
  }
  for (const f in portfolioFilter) {
    if (portfolioFilter[f].selected) {
      return true;
    }
  }
  for (const f in cyclesFilter) {
    if (cyclesFilter[f].selected) {
      return true;
    }
  }
  for (const f in statusFilter) {
    if (statusFilter[f].selected) {
      return true;
    }
  }
  for (const f in staffFilter) {
    if (staffFilter[f].selected) {
      return true;
    }
  }
  for (const f in dueDateFilter) {
    if (dueDateFilter[f].selected) {
      return true;
    }
  }
  for (const f in charityNavigatorFilter) {
    if (charityNavigatorFilter[f].selected) {
      return true;
    }
  }
  for (const f in investmentFilter) {
    if (investmentFilter[f].selected) {
      return true;
    }
  }
  return false;
};

interface FeedbackDashboardProps {
  type?: 'donor' | 'TI staff';
}

const FeedbackDashboard: React.FC<FeedbackDashboardProps> = ({
  type = 'TI staff'
}) => {
  const client = useClient();
  const [feedbackRows, setFeedbackRows] = useState<
    FeedbackDashboardCardProps[]
  >([]);
  const [feedbackRequestState, makeRequest] = useGraphQLRequest<{
    reportsTodo: FeedbackApi[];
  }>();
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [donorNamesApplied, setDonorNamesApplied] = useState<FeedbackFilters>({
    ...tempDonorName
  });
  const [defaultDonorNames, setDefaultDonorNames] = useState<
    FeedbackFilters
  >({});
  const [defaultRelationshipManager, setDefaultRelationshipManager] = useState<
    FeedbackFilters
  >({});
  const [relationshipManagerApplied, setRelationshipManagerApplied] = useState<
    FeedbackFilters
  >({});
  const [defaultPortfolioNames, setDefaultPortfolioName] = useState<
    FeedbackFilters
  >({});
  const [portfolioNamesApplied, setPortfolioNameApplied] = useState<
    FeedbackFilters
  >({});
  const [cyclesApplied, setCyclesApplied] = useState<FeedbackFilters>({
    ...tempCycleType
  });
  const [statusApplied, setStatusApplied] = useState<FeedbackFilters>({
    ...tempStatus(type)
  });
  const [trueImpactStaffApplied, setTrueImpactStaffApplied] = useState<
    FeedbackFilters
  >({
    ...tempTrueImpactStaff
  });
  const [dueDateApplied, setDueDateApplied] = useState<FeedbackFilters>({
    ...tempDueDate()
  });
  const [charityNavigatorApplied, setCharityNavigatorApplied] = useState<
    FeedbackFilters
  >({
    ...tempCharityNavigator
  });
  const [yearsApplied, setYearsApplied] = useState<FeedbackFilters>({
    ...tempYear(),
  });
  const [sortByApplied, setSortByApplied] = useState<FeedbackFilters>(
    getDefaultSortBy(type)
  );
  const [anyFiltersApplied, setAnyFiltersApplied] = useState<boolean>(false);
  const [feedbackRequestCompleted, setFeedbackRequestCompleted] = useState(false);

  useEffect(() => {
    if (client) {
      getRelationshipManager(client, type)
        .then(mapRelationshipFilters)
        .then(x => {
          setRelationshipManagerApplied(x.data.filters);
          setDefaultRelationshipManager(x.data.filters);
          return x;
        });
      getPortfolioFilters(client, type)
        .then(mapPortfolioFilters)
        .then(x => {
          setPortfolioNameApplied(x.data.filters);
          setDefaultPortfolioName(x.data.filters);
          return x;
        });
      getDonorsFilters(client)
        .then(mapDonorFilters)
        .then(x => {
          setDonorNamesApplied(x.data.filters);
          setDefaultDonorNames(x.data.filters);
          return x;
        });
    }
  }, []);
  useEffect(() => {
    if (feedbackRequestState.type === 'REQUEST_SUCCESS') {
      const feedbacks = feedbackMapFromApi(
        feedbackRequestState.data.reportsTodo
      ).feedbackRows;
      if (pageNumber === 1) {
        setFeedbackRows(feedbacks);
      } else {
        setFeedbackRows(f => [...f, ...feedbacks]);
      }
      setHasMore(feedbacks.length !== 0);
    }
  }, [feedbackRequestState.type, setFeedbackRows, setHasMore]);

  useEffect(() => {
    makeRequest(
      getFeedback(
        client as ApolloClient<object>,
        1,
        getSelectedSort(sortByApplied),
        undefined,
        searchTerm,
        {
          donorNameFilters: donorNamesApplied,
          cyclesFilters: cyclesApplied,
          portfolioNameFilters: portfolioNamesApplied,
          relationshipFilters: relationshipManagerApplied,
          staffFilters: trueImpactStaffApplied,
          dueDateFilters: dueDateApplied,
          yearFilters: yearsApplied,
          statusFilters: statusApplied,
          charityNavigatorFilters: charityNavigatorApplied
        }
      )
    );
    setPageNumber(1); // reset
  }, [
    searchTerm,
    donorNamesApplied,
    relationshipManagerApplied,
    portfolioNamesApplied,
    cyclesApplied,
    statusApplied,
    trueImpactStaffApplied,
    dueDateApplied,
    charityNavigatorApplied,
    yearsApplied,
    sortByApplied,
    setPageNumber
  ]);

  useEffect(() => {
    if (pageNumber === 1) return;
    const sortBy = getSelectedSort(sortByApplied);
    makeRequest(
      getFeedback(client!, pageNumber, sortBy, undefined, searchTerm, {
        donorNameFilters: donorNamesApplied,
        cyclesFilters: cyclesApplied,
        portfolioNameFilters: portfolioNamesApplied,
        relationshipFilters: relationshipManagerApplied,
        staffFilters: trueImpactStaffApplied,
        dueDateFilters: dueDateApplied,
        charityNavigatorFilters: charityNavigatorApplied,
        yearFilters: yearsApplied,
        statusFilters: statusApplied,
      }),
    );
  }, [pageNumber, sortByApplied]);

  useEffect(() => {
    setAnyFiltersApplied(
      isAnyFilterApplied(
        donorNamesApplied,
        relationshipManagerApplied,
        portfolioNamesApplied,
        cyclesApplied,
        statusApplied,
        trueImpactStaffApplied,
        dueDateApplied,
        yearsApplied,
        charityNavigatorApplied
      ),
    );
  }, [
    donorNamesApplied,
    relationshipManagerApplied,
    portfolioNamesApplied,
    cyclesApplied,
    statusApplied,
    trueImpactStaffApplied,
    dueDateApplied,
    yearsApplied,
    charityNavigatorApplied
  ]);

  const handleApplyAllFilters = useCallback(
    (filters: ApplyFilters) => {
      setDonorNamesApplied({
        ...tempDonorName,
        ...filters.donorNameFilters
      });
      setRelationshipManagerApplied({
        ...defaultRelationshipManager,
        ...filters.relationshipFilters
      });
      setPortfolioNameApplied({
        ...defaultPortfolioNames,
        ...filters.portfolioNameFilters
      });
      setCyclesApplied({
        ...tempCycleType,
        ...filters.cyclesFilters
      });
      setStatusApplied({
        ...tempStatus(undefined),
        ...filters.statusFilters
      });
      setTrueImpactStaffApplied({
        ...tempTrueImpactStaff,
        ...filters.staffFilters
      });
      setDueDateApplied({
        ...tempDueDate(),
        ...filters.dueDateFilters
      });
      setCharityNavigatorApplied({
        ...tempCharityNavigator,
        ...filters.charityNavigatorFilters
      });
      setDueDateApplied({ ...tempDueDate(), ...filters.dueDateFilters });
      setYearsApplied({ ...tempYear(), ...filters.yearFilters });
    },
    [
      defaultPortfolioNames,
      defaultRelationshipManager,
      setDonorNamesApplied,
      setRelationshipManagerApplied,
      setPortfolioNameApplied,
      setCyclesApplied,
      setStatusApplied,
      setTrueImpactStaffApplied,
      setDueDateApplied,
      setYearsApplied,
      setCharityNavigatorApplied
    ],
  );

  const onClearDonorNameFilter = useCallback(() => {
    setDonorNamesApplied({ ...defaultDonorNames });
  }, [setDonorNamesApplied]);
  const onClearRelationshipFilter = useCallback(() => {
    setRelationshipManagerApplied({ ...defaultRelationshipManager });
  }, [defaultRelationshipManager, setRelationshipManagerApplied]);
  const onClearPortfolioFilter = useCallback(() => {
    setPortfolioNameApplied({ ...defaultPortfolioNames });
  }, [defaultPortfolioNames, setPortfolioNameApplied]);
  const onClearCycleFilter = useCallback(() => {
    setCyclesApplied({ ...tempCycleType });
  }, [setCyclesApplied]);
  const onClearStatusFilter = useCallback(() => {
    setStatusApplied({ ...tempStatus(undefined) });
  }, [setStatusApplied]);
  const onClearTrueImpactStaffFilter = useCallback(() => {
    setTrueImpactStaffApplied({ ...tempTrueImpactStaff });
  }, [setTrueImpactStaffApplied]);
  const onClearDueDateFilter = useCallback(() => {
    setDueDateApplied({ ...tempDueDate() });
  }, [setDueDateApplied]);
  const onClearCharityNavigatorFilter = useCallback(() => {
    setCharityNavigatorApplied({ ...tempCharityNavigator });
  }, [setCharityNavigatorApplied]);
  const onClearYearFilter = useCallback(() => {
    setYearsApplied({ ...tempYear() });
  }, [setYearsApplied]);
  const onClearSortByFilter = useCallback(() => {
    setSortByApplied(getDefaultSortBy(type));
  }, [setSortByApplied, type]);

  const onRemoveDonorNameFilter = useCallback(
    (key: string) => {
      setDonorNamesApplied(s => {
        const newState = { ...s };
        newState[key].selected = false;
        return newState;
      });
    },
    [setDonorNamesApplied]
  );
  const onRemoveRelationshipFilter = useCallback(
    (key: string) => {
      setRelationshipManagerApplied(s => {
        const newState = { ...s };
        newState[key].selected = false;
        return newState;
      });
    },
    [setRelationshipManagerApplied]
  );
  const onRemovePortfolioFilter = useCallback(
    (key: string) => {
      setPortfolioNameApplied(s => {
        const newState = { ...s };
        newState[key].selected = false;
        return newState;
      });
    },
    [setPortfolioNameApplied]
  );
  const onRemoveCycleFilter = useCallback(
    (key: string) => {
      setCyclesApplied(s => {
        const newState = { ...s };
        newState[key].selected = false;
        return newState;
      });
    },
    [setCyclesApplied]
  );
  const onRemoveStatusFilter = useCallback(
    (key: string) => {
      setStatusApplied(s => {
        const newState = { ...s };
        newState[key].selected = false;
        return newState;
      });
    },
    [setStatusApplied]
  );
  const onRemoveTrueImpactStaffFilter = useCallback(
    (key: string) => {
      setTrueImpactStaffApplied(s => {
        const newState = { ...s };
        newState[key].selected = false;
        return newState;
      });
    },
    [setTrueImpactStaffApplied]
  );
  const onRemoveCharityNavigatorFilter = useCallback(
    (key: string) => {
      setCharityNavigatorApplied(s => {
        const newState = { ...s };
        newState[key].selected = false;
        return newState;
      });
    },
    [setCharityNavigatorApplied]
  );
  const onRemoveYearsFilter = useCallback(
    (key: string) => {
      setYearsApplied(s => {
        const newState = { ...s };
        newState[key].selected = false;
        return newState;
      });
    },
    [setYearsApplied],
  );


  const handleClearAllFilters = useCallback(() => {
    onClearDonorNameFilter();
    onClearRelationshipFilter();
    onClearPortfolioFilter();
    onClearCycleFilter();
    onClearStatusFilter();
    onClearTrueImpactStaffFilter();
    onClearDueDateFilter();
    onClearCharityNavigatorFilter();
    // onClearSortByFilter();
  }, [
    onClearDonorNameFilter,
    onClearRelationshipFilter,
    onClearPortfolioFilter,
    onClearCycleFilter,
    onClearStatusFilter,
    onClearTrueImpactStaffFilter,
    onClearDueDateFilter,
    onClearCharityNavigatorFilter
    // onClearSortByFilter
  ]);

  const donorNames = useMemo(
    () => ({
      filters: donorNamesApplied,
      applyFilter: setDonorNamesApplied,
      onClear: onClearDonorNameFilter,
      onRemove: onRemoveDonorNameFilter
    }),
    [
      donorNamesApplied,
      setDonorNamesApplied,
      onClearDonorNameFilter,
      onRemoveDonorNameFilter
    ]
  );
  const relationships = useMemo(
    () => ({
      filters: relationshipManagerApplied,
      applyFilter: setRelationshipManagerApplied,
      onClear: onClearRelationshipFilter,
      onRemove: onRemoveRelationshipFilter
    }),
    [
      relationshipManagerApplied,
      setRelationshipManagerApplied,
      onClearRelationshipFilter,
      onRemoveRelationshipFilter
    ]
  );
  const portfolioNames = useMemo(
    () => ({
      filters: portfolioNamesApplied,
      applyFilter: setPortfolioNameApplied,
      onClear: onClearPortfolioFilter,
      onRemove: onRemovePortfolioFilter
    }),
    [
      portfolioNamesApplied,
      setPortfolioNameApplied,
      onClearPortfolioFilter,
      onRemovePortfolioFilter
    ]
  );
  const cycles = useMemo(
    () => ({
      filters: cyclesApplied,
      applyFilter: setCyclesApplied,
      onClear: onClearCycleFilter,
      onRemove: onRemoveCycleFilter
    }),
    [cyclesApplied, setCyclesApplied, onClearCycleFilter, onRemoveCycleFilter]
  );
  const status = useMemo(
    () => ({
      filters: statusApplied,
      applyFilter: setStatusApplied,
      onClear: onClearStatusFilter,
      onRemove: onRemoveStatusFilter
    }),
    [statusApplied, setStatusApplied, onClearStatusFilter, onRemoveStatusFilter]
  );
  const trueImpactStaff = useMemo(
    () => ({
      filters: trueImpactStaffApplied,
      applyFilter: setTrueImpactStaffApplied,
      onClear: onClearTrueImpactStaffFilter,
      onRemove: onRemoveTrueImpactStaffFilter
    }),
    [
      trueImpactStaffApplied,
      setTrueImpactStaffApplied,
      onClearTrueImpactStaffFilter,
      onRemoveTrueImpactStaffFilter
    ]
  );
  const years = useMemo(
    () => ({
      filters: yearsApplied,
      applyFilter: setYearsApplied,
      onClear: onClearYearFilter,
      onRemove: onRemoveYearsFilter,
    }),
    [
      yearsApplied,
      setYearsApplied,
      onClearYearFilter,
      onRemoveYearsFilter
    ],
  );
  const dueDates = useMemo(
    () => ({
      filters: dueDateApplied,
      applyFilter: (key: string) => {
        const newState = {
          ...tempDueDate()
        };
        newState[key].selected = true;
        setDueDateApplied(newState);
      },
      onClear: onClearDueDateFilter
    }),
    [dueDateApplied, setDueDateApplied, onClearDueDateFilter]
  );
  const charityNavigator = useMemo(
    () => ({
      filters: charityNavigatorApplied,
      applyFilter: setCharityNavigatorApplied,
      onClear: onClearCharityNavigatorFilter,
      onRemove: onRemoveCharityNavigatorFilter
    }),
    [
      charityNavigatorApplied,
      setCharityNavigatorApplied,
      onClearCharityNavigatorFilter,
      onRemoveCharityNavigatorFilter
    ]
  );
  const sortBy = useMemo(
    () => ({
      filters: sortByApplied,
      applyFilter: (key: string) => {
        const newState = tempSortBy(type);
        newState[key].selected = true;
        setSortByApplied(newState);
      },
      onClear: onClearSortByFilter
    }),
    [sortByApplied, type, setSortByApplied, onClearSortByFilter]
  );

  const loadMore = useCallback(() => {
    setPageNumber(p => p + 1);
  }, [setPageNumber]);

  return (
    <StyledDiv>
      <FeedbackDashboardFilterBar
        type={type}
        handleClearAllFilters={handleClearAllFilters}
        handleSearchTerm={setSearchTerm}
        handleApplyAllFilters={handleApplyAllFilters}
        className="filter-bar"
        anyFiltersApplied={anyFiltersApplied}
        donorNames={donorNames}
        years={years}
        relationships={relationships}
        portfolioNames={portfolioNames}
        cycles={cycles}
        status={status}
        trueImpactStaff={trueImpactStaff}
        dueDates={dueDates}
        charityNavigator={charityNavigator}
        sortBy={sortBy}
      />
      <FeedbackRows
        loadingFirstPage={pageNumber === 1}
        requestState={feedbackRequestState}
        rows={feedbackRows}
        loadMore={hasMore ? loadMore : undefined}
      />
    </StyledDiv>
  );
};

export default FeedbackDashboard;
